import * as Sentry from "@sentry/nextjs";
import { useSession } from "@/components/providers/session";
import dynamic from "next/dynamic";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { recordConversion, Conversion } from "@/utils/conversions";
import { klayAction } from "@/utils/klay";
const Modal = dynamic(() => import("@/components/molecules/modal"));

export default function Callback({ entry, event }) {
  const [state, setState] = useState({
    complete: null,
  });
  const session = useSession();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
  });

  const courses =
    session && session.selectedCourses && session.selectedCourses.length > 0
      ? session.selectedCourses.map((course) => {
          return { id: course.event.id, title: course.fields.title };
        })
      : [];

  const onSubmit = async (data) => {
    try {
      const req = await fetch("/api/callback/", {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...data, courses }),
      });
      const res = await req.json();
      if (res && res.success) {
        recordConversion({
          send_to: Conversion.callback,
        });
        (window as any).fbq && (window as any).fbq("track", "Contact");
        (window as any).heap &&
          (window as any).heap.addUserProperties({
            ...data,
          });
        (window as any).heap &&
          (window as any).heap.track("Callback Request", {
            ...data,
          });
        // @ts-ignore
        klaviyo &&
          // @ts-ignore
          klaviyo.push([
            "identify",
            {
              $email: data.email,
              $first_name: data.first_name,
              $last_name: data.last_name,
              $phone_number: data.phone_number,
            },
          ]);

        const $event_id = event ? event.id : null;
        const $event_price = event ? event.standardPrice / 100 : null;

        // @ts-ignore
        klaviyo &&
          // @ts-ignore
          klaviyo.push([
            "track",
            "Callback Request",
            {
              $email: data.email,
              $first_name: data.first_name,
              $last_name: data.last_name,
              $phone_number: data.phone_number,
              $path_name: window.location.pathname,
              $event_id,
              $event_price,
              ...klayAction(entry),
            },
          ]);
        setState({
          complete: true,
        });
      }
    } catch (err) {
      console.log(err);
      Sentry.captureException(err);
    }
  };

  return (
    <>
      <div className="bg-white px-4 sm:px-6 lg:px-8 lg:pb-12" id="callback">
        <div className="relative mx-auto">
          <svg
            className="absolute left-full transform translate-x-1/2"
            width="404"
            height="404"
            fill="none"
            viewBox="0 0 404 404"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="85737c0e-0916-41d7-917f-596dc7edfa27"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x="0"
                  y="0"
                  width="4"
                  height="4"
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width="404"
              height="404"
              fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)"
            />
          </svg>
          <svg
            className="absolute right-full bottom-0 transform -translate-x-1/2"
            width="404"
            height="404"
            fill="none"
            viewBox="0 0 404 404"
            aria-hidden="true"
          >
            <defs>
              <pattern
                id="85737c0e-0916-41d7-917f-596dc7edfa27"
                x="0"
                y="0"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <rect
                  x="0"
                  y="0"
                  width="4"
                  height="4"
                  className="text-gray-200"
                  fill="currentColor"
                />
              </pattern>
            </defs>
            <rect
              width="404"
              height="404"
              fill="url(#85737c0e-0916-41d7-917f-596dc7edfa27)"
            />
          </svg>

          {state.complete && (
            <div className="">
              <div className="bg-white">
                <div className="max-w-7xl mx-auto pt-24 pb-24 px-4 sm:px-6 md:py-16 lg:py-24 lg:px-8">
                  <div className="text-center">
                    <p className="max-w-xl mt-5 mx-auto text-xl text-gray-500">
                      Your callback request has been received and our team will
                      be back in touch as soon as possible.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!state.complete && (
            <div className="bg-white">
              <div className="relative sm:py-8">
                <div aria-hidden="true" className="hidden sm:block">
                  <div className="absolute inset-y-0 left-0 -ml-16 w-1/2 bg-gray-50 rounded-r-3xl"></div>
                  <svg
                    className="absolute top-8 left-1/2 -ml-3"
                    width="404"
                    height="392"
                    fill="none"
                    viewBox="0 0 404 392"
                  >
                    <defs>
                      <pattern
                        id="8228f071-bcee-4ec8-905a-2a059a2cc4fb"
                        x="0"
                        y="0"
                        width="20"
                        height="20"
                        patternUnits="userSpaceOnUse"
                      >
                        <rect
                          x="0"
                          y="0"
                          width="4"
                          height="4"
                          className="text-gray-200"
                          fill="currentColor"
                        />
                      </pattern>
                    </defs>
                    <rect
                      width="404"
                      height="392"
                      fill="url(#8228f071-bcee-4ec8-905a-2a059a2cc4fb)"
                    />
                  </svg>
                </div>
                <div className="mx-auto px-4 sm:max-w-3xl sm:px-6 lg:max-w-7xl lg:px-8">
                  <div className="relative rounded-2xl px-6 py-10 bg-brand-400 overflow-hidden shadow-xl sm:px-12 sm:py-20">
                    <div
                      aria-hidden="true"
                      className="absolute inset-0 -mt-72 sm:-mt-32 md:mt-0"
                    >
                      <svg
                        className="absolute inset-0 h-full w-full"
                        preserveAspectRatio="xMidYMid slice"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 1463 360"
                      >
                        <path
                          className="text-brand-200 text-opacity-50"
                          fill="currentColor"
                          d="M-82.673 72l1761.849 472.086-134.327 501.315-1761.85-472.086z"
                        />
                        <path
                          className="text-brand-500 text-opacity-50"
                          fill="currentColor"
                          d="M-217.088 544.086L1544.761 72l134.327 501.316-1761.849 472.086z"
                        />
                      </svg>
                    </div>
                    <div className="relative">
                      <div className="sm:text-center">
                        <h2 className="text-3xl font-extrabold text-gray-800 tracking-tight sm:text-4xl">
                          Want to discuss options or have questions?
                        </h2>
                        <p className="mt-6 mx-auto max-w-2xl text-lg text-gray-800">
                          Schedule a call with An advisor to have all your
                          questions answered and options for training reviewed.
                        </p>
                      </div>
                      <form
                        onSubmit={handleSubmit(onSubmit)}
                        className="mt-12 sm:mx-auto sm:max-w-lg sm:flex sm:flex-col"
                      >
                        <div className="">
                          <label htmlFor="cta_name" className="sr-only">
                            First name
                          </label>
                          <input
                            autoComplete="given-name"
                            id="cta_name"
                            type="text"
                            className="block w-full border border-transparent rounded-md px-5 py-3 text-base text-gray-900 placeholder-gray-500 shadow-sm focus:outline-none focus:border-transparent focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-600"
                            name="first_name"
                            placeholder="Enter your name"
                            {...register("first_name", { required: true })}
                          />
                        </div>
                        <div className="mt-4">
                          <label htmlFor="cta_last" className="sr-only">
                            Last name
                          </label>
                          <input
                            autoComplete="family-name"
                            id="cta_last"
                            type="text"
                            className="block w-full border border-transparent rounded-md px-5 py-3 text-base text-gray-900 placeholder-gray-500 shadow-sm focus:outline-none focus:border-transparent focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-600"
                            name="first_name"
                            placeholder="Enter your last name"
                            {...register("last_name", { required: true })}
                          />
                        </div>
                        <div className="mt-4">
                          <label htmlFor="cta_number" className="sr-only">
                            Phone number
                          </label>
                          <input
                            autoComplete="phone"
                            name="phone_number"
                            id="cta_number"
                            type="text"
                            className="block w-full border border-transparent rounded-md px-5 py-3 text-base text-gray-900 placeholder-gray-500 shadow-sm focus:outline-none focus:border-transparent focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-600"
                            placeholder="Enter your number"
                            {...register("phone_number", { required: true })}
                          />
                        </div>
                        <div className="mt-4">
                          <label htmlFor="cta_email" className="sr-only">
                            Email address
                          </label>
                          <input
                            autoComplete="email"
                            name="email"
                            id="cta_email"
                            type="email"
                            className="block w-full border border-transparent rounded-md px-5 py-3 text-base text-gray-900 placeholder-gray-500 shadow-sm focus:outline-none focus:border-transparent focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-600"
                            placeholder="Enter your email"
                            {...register("email", { required: true })}
                          />
                        </div>
                        <div className="mt-4">
                          <button
                            type="submit"
                            className="transition-all duration-75 block w-full rounded-md border border-transparent px-5 py-3 bg-brand-600 text-base font-medium text-white shadow hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-brand-600 sm:px-10"
                          >
                            Request callback
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <Modal
        open={state.complete}
        close={() => {
          setState({ complete: false });
          document.body.style.overflow = "auto";
        }}
        full={state.complete}
        xlarge={!state.complete}
      >
         <div className="h-full">
            <div className="bg-white">
              <div className="max-w-7xl mx-auto h-full">
                <iframe
                  src="https://calendar.google.com/calendar/appointments/schedules/AcZssZ28xd3NPsZQJIxeuaTCep2WJENG0LOYVTLTDZchVdRmkT3mhwe09k6LPa8iuTBuXaUEZY0iYOBQ?gv=true"
                  style={{ border: 0 }}
                  width="100%"
                  height="700"
                  loading="lazy"
                  frameBorder="0"
                ></iframe>
              </div>
            </div>
          </div>
      </Modal>
    </>
  );
}
